/**
 * usePollingHandler - 轮询结果处理器
 *
 * 职责：
 * 1. 监听轮询完成事件
 * 2. 处理 pendingResult
 * 3. 将轮询结果同步到 UI
 *
 * 注意：这是一个副作用 Hook，用于解耦轮询逻辑与 UI 更新
 */

import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { logger } from '@/lib/logger'
import { useEngineStore } from '../stores/use-engine-store'
import type { ServerCurrentScene } from '../lib/adapters'

interface UsePollingHandlerOptions {
  /**
   * 当前 sessionId
   */
  sessionId: string | undefined

  /**
   * 轮询结果处理回调
   */
  onResult: (scene: ServerCurrentScene, sessionId: string) => void

  /**
   * 交易记录失效回调（用于触发余额更新）
   */
  onTransactionInvalidate?: () => void
}

export const usePollingHandler = (options: UsePollingHandlerOptions) => {
  const { sessionId, onResult, onTransactionInvalidate } = options

  // 获取 choice 切片状态
  const {
    pendingResult,
    sessionId: pollingSessionId,
    clearPendingResult,
  } = useEngineStore(
    useShallow((s) => ({
      pendingResult: s.choice.pendingResult,
      sessionId: s.choice.sessionId,
      clearPendingResult: s.choice.clearPendingResult,
    }))
  )

  // 监听轮询结果
  // 当轮询完成时，pendingResult 会被设置，使用乐观锁防止多实例重复处理
  useEffect(() => {
    if (!pendingResult || !pollingSessionId || pollingSessionId !== sessionId) {
      return
    }

    // 乐观锁：直接从 store 读取并立即清除
    const choiceState = useEngineStore.getState().choice
    const resultToProcess = choiceState.pendingResult

    if (!resultToProcess) {
      logger.debug('[usePollingHandler] pendingResult 已被其他实例处理')
      return
    }

    // 立即清除，防止其他实例重复处理
    clearPendingResult()

    logger.info(
      '[usePollingHandler] 检测到轮询结果，正在处理',
      resultToProcess
    )

    // 调用结果处理回调
    if (sessionId) {
      onResult(resultToProcess as ServerCurrentScene, sessionId)
    }

    // 轮询结束，结果已到，此时后端通常已完成计费，触发交易记录更新
    if (onTransactionInvalidate) {
      onTransactionInvalidate()
    }
  }, [
    pendingResult,
    pollingSessionId,
    sessionId,
    onResult,
    clearPendingResult,
    onTransactionInvalidate,
  ])
}
